/**
 * {@link ModuleStorage} for the `user` scope: delegates 1:1 to the configured
 * `ModuleStorageTransport`. One transport subscription per store instance, opened
 * lazily on the first `subscribe`, dropped when the last listener leaves, and moved
 * to the current transport when the runtime is reconfigured (`onConfigChange`). The
 * store remembers the write ids of its own `set`s and swallows their notifications,
 * so `subscribe` never fires for writes made through this instance.
 */
export const userScopeStore = (type, config, onConfigChange) => {
    const listeners = new Map();
    const ownWriteIds = new Set();
    let transportUnsub;
    let connecting = false;
    let generation = 0;
    const listenerCount = () => [...listeners.values()].reduce((n, set) => n + set.size, 0);
    const connect = async () => {
        if (transportUnsub || connecting)
            return;
        connecting = true;
        const gen = generation;
        const { transport } = await config();
        connecting = false;
        if (generation !== gen)
            return connect();
        if (transportUnsub || listenerCount() === 0)
            return;
        transportUnsub = transport.subscribe(type, (key, value, writeId) => {
            if (ownWriteIds.delete(writeId))
                return;
            const keyListeners = listeners.get(key);
            if (!keyListeners)
                return;
            for (const onChange of [...keyListeners])
                onChange(value);
        });
    };
    onConfigChange?.(() => {
        generation++;
        if (transportUnsub) {
            transportUnsub();
            transportUnsub = undefined;
        }
        if (listenerCount() > 0)
            void connect();
    });
    return {
        get: async (key) => {
            const { transport } = await config();
            return (await transport.get(type, key));
        },
        set: async (key, value) => {
            const { transport } = await config();
            ownWriteIds.add(await transport.set(type, key, value));
        },
        remove: async (key) => {
            const { transport } = await config();
            ownWriteIds.add(await transport.remove(type, key));
        },
        subscribe: (key, onChange) => {
            let keyListeners = listeners.get(key);
            if (!keyListeners) {
                keyListeners = new Set();
                listeners.set(key, keyListeners);
            }
            keyListeners.add(onChange);
            void connect();
            return () => {
                keyListeners.delete(onChange);
                if (keyListeners.size === 0)
                    listeners.delete(key);
                if (listenerCount() === 0 && transportUnsub) {
                    transportUnsub();
                    transportUnsub = undefined;
                }
            };
        },
    };
};
//# sourceMappingURL=userScopeStore.js.map